/**
 * Certificate — completion credential for the Financial Literacy track.
 *
 * Net-new surface (Lomi has no credential concept today). Renders a printable
 * certificate card, snapshots it to PNG via html-to-image (already a dep), and
 * offers download + native share. Neutral Lomi branding; a cobrand slot is
 * wired but left as Lomi-only for v0.
 *
 * Lives under components/finance/ so the whole Financial Literacy vertical is
 * easy to remove (delete the folder + the /finance route + the seed rows).
 */
import React, { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import Logo from '../Logo';
import './Certificate.css';

interface CertificateProps {
  studentName: string;
  courseTitle: string;
  accuracy: number;          // 0-100
  correctAnswers: number;
  totalQuestions: number;
  completedAt?: string;      // ISO date
  verifyId: string;          // short verification code
  cobrand?: string;          // optional partner name; null/undefined = Lomi only
  onClose?: () => void;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatDate(iso?: string): string {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

const Certificate: React.FC<CertificateProps> = ({
  studentName,
  courseTitle,
  accuracy,
  correctAnswers,
  totalQuestions,
  completedAt,
  verifyId,
  cobrand,
  onClose,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);

  const snapshot = async (): Promise<Blob | null> => {
    if (!cardRef.current) return null;
    const dataUrl = await toPng(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#ffffff',
    });
    const res = await fetch(dataUrl);
    return await res.blob();
  };

  const handleDownload = async () => {
    try {
      setBusy(true);
      const blob = await snapshot();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Lomi-Certificate-${studentName.replace(/\s+/g, '_')}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Certificate download failed', e);
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async () => {
    try {
      setBusy(true);
      const blob = await snapshot();
      if (!blob) return;
      const file = new File([blob], 'Lomi-Certificate.png', { type: 'image/png' });
      const nav = navigator as any;
      if (nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({
          files: [file],
          title: 'My Lomi Certificate',
          text: `I completed "${courseTitle}" on Lomi.`,
        });
      } else {
        await handleDownload();
      }
    } catch (e) {
      // user cancelled or unsupported — silent
    } finally {
      setBusy(false);
    }
  };

  const scoreLabel = `${Math.round(accuracy)}%`;

  return (
    <div className="cert-overlay" role="dialog" aria-label="Certificate of completion">
      <div className="cert-modal">
        <div className="cert-confetti">🎉</div>
        <h2 className="cert-congrats">Congratulations, {studentName.split(' ')[0]}!</h2>
        <p className="cert-sub">You've earned your certificate. Download it or share it.</p>

        {/* The certificate artifact (what gets snapshotted) */}
        <div className="cert-card" ref={cardRef}>
          <div className="cert-card-border">
            <div className="cert-brand">
              <Logo height={40} withAmharic />
            </div>

            <div className="cert-kicker">CERTIFICATE OF COMPLETION</div>

            <div className="cert-presented">This certifies that</div>
            <div className="cert-name">{studentName}</div>

            <div className="cert-body">
              has successfully completed the financial literacy course
            </div>
            <div className="cert-course">{courseTitle}</div>

            <div className="cert-stats">
              <div className="cert-stat">
                <div className="cert-stat-val">{scoreLabel}</div>
                <div className="cert-stat-label">Final Score</div>
              </div>
              <div className="cert-stat-divider" />
              <div className="cert-stat">
                <div className="cert-stat-val">{correctAnswers}/{totalQuestions}</div>
                <div className="cert-stat-label">Questions Correct</div>
              </div>
            </div>

            <div className="cert-footer">
              <div className="cert-foot-col">
                <div className="cert-foot-val">{formatDate(completedAt)}</div>
                <div className="cert-foot-label">Date</div>
              </div>
              <div className="cert-seal">
                <div className="cert-seal-ring">
                  <span>✔</span>
                </div>
              </div>
              <div className="cert-foot-col cert-foot-right">
                <div className="cert-foot-val">{verifyId}</div>
                <div className="cert-foot-label">Verification ID</div>
              </div>
            </div>

            <div className="cert-issued">
              Issued by Lomi{cobrand ? `  ·  in partnership with ${cobrand}` : ''}  ·  lomistudy.com
            </div>
          </div>
        </div>

        <div className="cert-actions">
          <button className="cert-btn cert-btn-primary" onClick={handleDownload} disabled={busy}>
            {busy ? 'Preparing…' : '⬇  Download'}
          </button>
          <button className="cert-btn cert-btn-secondary" onClick={handleShare} disabled={busy}>
            ↗  Share
          </button>
          {onClose && (
            <button className="cert-btn cert-btn-ghost" onClick={onClose} disabled={busy}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Certificate;
